/**
 * DataManagementSection - Data management component
 *
 * Pure presentational component showing clear cache and history options.
 * All handlers and logic in parent.
 */
import { View } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import { TierBody } from "@dramadetector/ui";
import { GlassCard } from "../../../components/glass";
import { SettingsItem } from "../../../components/SettingsItem";

interface DataManagementSectionProps {
  /** Clear cache callback */
  onClearCache: () => void;
  /** Clear history callback */
  onClearHistory: () => void;
  /** Delete all user data callback (GDPR Art. 17) */
  onDeleteAllData?: () => void;
  /** Whether user is authenticated (delete all data requires auth) */
  isAuthenticated?: boolean;
}

export function DataManagementSection({
  onClearCache,
  onClearHistory,
  onDeleteAllData,
  isAuthenticated = false,
}: DataManagementSectionProps) {
  const { t } = useTranslation('settings');

  return (
    <View className="mt-6">
      <TierBody size="xs" weight="semibold" className="text-gray-500 uppercase px-4 mb-2">
        {t('sections.data')}
      </TierBody>
      <GlassCard>
        <SettingsItem
          icon="🗑️"
          label={t('cache.clear')}
          description={t('cache.description')}
          onPress={onClearCache}
        />
        <View className="border-t border-gray-200" />
        <SettingsItem
          icon="📝"
          label={t('history.clear')}
          description={t('history.description')}
          onPress={onClearHistory}
          destructive
        />
        {isAuthenticated && onDeleteAllData && (
          <>
            <View className="border-t border-gray-200" />
            <SettingsItem
              icon="⚠️"
              label={t('delete_data.title')}
              description={t('delete_data.description')}
              onPress={onDeleteAllData}
              destructive
              testID="delete-all-data-button"
            />
          </>
        )}
      </GlassCard>
    </View>
  );
}
